/**
 * 通话服务
 * 统一通话信令处理
 */
import { getTIM } from '../sdk/tim/index.js'
import { eventBus, SDK_EVENTS } from '../sdk/eventBus.js'
import TencentCloudChat from '@tencentcloud/chat'
import { SDK_ERRORS } from '../errors/codes.js'
import { wrapSDKError, handleSDKError } from '../errors/handler.js'
import { logger } from '../utils/logger.js'

const log = logger.module('CallService')

// 通话动作常量
export const CALL_ACTIONS = {
  INVITE: 0,       // 呼叫
  CANCEL: 1,       // 取消呼叫
  REJECT: 2,       // 拒绝
  TIMEOUT: 3,      // 超时
  ACCEPT: 4,       // 接听
  HANGUP: 5,       // 挂断
  BUSY: 6,         // 忙碌
  ERROR: -2        // 错误
}

// 通话类型
export const CALL_TYPES = {
  VIDEO: 'video',
  AUDIO: 'audio'
}

/**
 * 初始化通话信令监听
 * 需在 TIM SDK ready 后调用
 */
export function initCallListener() {
  const tim = getTIM()
  if (!tim) {
    log.warn('TIM not initialized, skip call listener init')
    return
  }

  const Event = TencentCloudChat.EVENT

  tim.on(Event.MESSAGE_RECEIVED, (event) => {
    const messages = event.data || []
    messages.forEach(msg => {
      if (msg.type === TencentCloudChat.TYPES.MSG_CUSTOM) {
        const parsed = _parseCustomPayload(msg)

        // 检查是否为通话信令（通过 action 字段判断）
        if (parsed && typeof parsed.action === 'number') {
          // 已有 version:3 的 H5 格式
          if (parsed.version === 3) {
            log.debug('Received H5 call signal:', parsed.action)
            eventBus.emit(SDK_EVENTS.CALL_SIGNAL, { msg, signal: parsed })
          }
          // 没有 version 字段，但有 action（mp-ipmini 格式）
          else if (!parsed.version) {
            log.debug('Received mp-ipmini call signal:', parsed.action)
            // 转换为 version:3 格式
            const signal = _convertFromMpFormat(parsed)
            eventBus.emit(SDK_EVENTS.CALL_SIGNAL, { msg, signal })
          }
        }
        // status_update 格式（用于通话状态显示）
        else if (parsed?.type === 'status_update') {
          const signal = _convertFromStatusUpdate(msg, parsed)
          log.debug('Converted status_update call signal:', signal.action)
          eventBus.emit(SDK_EVENTS.CALL_SIGNAL, { msg, signal })
        }
      }
    })
  })
}

/**
 * 发送通话信令
 * @param {string} conversationID
 * @param {Object} signal
 */
export async function sendCallSignal(conversationID, signal) {
  const tim = getTIM()
  if (!tim) {
    const err = wrapSDKError(SDK_ERRORS.TIM_NOT_INIT, null, '发送通话信令失败：SDK未初始化')
    throw err
  }

  const toId = conversationID.replace(/^C2C/, '')
  try {
    const msg = tim.createCustomMessage({
      to: toId,
      conversationType: TencentCloudChat.TYPES.CONV_C2C,
      payload: {
        data: JSON.stringify(signal),
        description: signal.call_type === 'video' ? '[视频通话]' : '[语音通话]'
      }
    })

    const res = await tim.sendMessage(msg)
    log.debug('Call signal sent:', signal.action)
    return res.data.message
  } catch (err) {
    const appErr = wrapSDKError(SDK_ERRORS.TIM_SEND_MSG_FAILED, err, `发送通话信令失败：${err.message}`)
    handleSDKError(err, SDK_ERRORS.TIM_SEND_MSG_FAILED, { conversationID, action: signal.action })
    throw appErr
  }
}

/**
 * 构造通话信令
 * @param {Object} options
 */
export function buildCallSignal(options) {
  const {
    callId,
    roomId,
    action,
    callType,
    duration = 0,
    invitedList = []
  } = options

  return {
    call_id: callId,
    version: 3,
    room_id: String(roomId),
    action,
    duration,
    invited_list: invitedList,
    call_type: callType
  }
}

/**
 * 发送状态更新消息
 * @param {string} conversationID
 * @param {string} status - 'calling' | ''
 * @param {string} userId
 */
export async function sendStatusUpdate(conversationID, status, userId) {
  const tim = getTIM()
  if (!tim) {
    log.warn('TIM not initialized, skip status update')
    return
  }

  const toId = conversationID.replace(/^C2C/, '')

  try {
    const msg = tim.createCustomMessage({
      to: toId,
      conversationType: TencentCloudChat.TYPES.CONV_C2C,
      payload: {
        data: JSON.stringify({ type: 'status_update', status, userID: userId }),
        description: '',
        extension: 'status_update'
      }
    })

    await tim.sendMessage(msg)
    log.debug('Status update sent:', status)
  } catch (err) {
    log.warn('sendStatusUpdate failed:', err.message)
    // 静默处理，不影响主流程
  }
}

// ============ 私有方法 ============

/**
 * 解析自定义消息 payload
 */
function _parseCustomPayload(msg) {
  const raw = msg.payload?.data || ''
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * 将小程序 status_update 格式转换为 version:3 格式
 */
function _convertFromStatusUpdate(msg, parsed) {
  return {
    version: 3,
    action: parsed.status === 'calling' ? CALL_ACTIONS.INVITE :
            parsed.status === '' ? CALL_ACTIONS.HANGUP : CALL_ACTIONS.INVITE,
    room_id: parsed.room_id || '',
    call_type: parsed.call_type || 'video',
    call_id: parsed.call_id || '',
    invited_list: [msg.from],
    duration: 0
  }
}

/**
 * 将 mp-ipmini 直接发送的 args 格式转换为 version:3 格式
 * mp-ipmini 发送的 payload.data 是 args 对象的 JSON，包含 call_id, room_id, action 等
 */
function _convertFromMpFormat(parsed) {
  return {
    version: 3,
    call_id: parsed.call_id || '',
    room_id: parsed.room_id || '',
    action: parsed.action,
    duration: parsed.duration || 0,
    invited_list: parsed.invited_list || [],
    call_type: parsed.call_type || 'video'
  }
}

export default {
  CALL_ACTIONS,
  CALL_TYPES,
  initCallListener,
  sendCallSignal,
  buildCallSignal,
  sendStatusUpdate
}
